"use client";
import { useState } from "react";
import { motion } from "motion/react";
import Timeline from "./Timeline";
import TimelineItem from "./TimelineItem";
import { useFilter } from "@/app/context/FilterContext";

export interface TimelineEventData {
  id: number;
  date: string;
  header: string;
  description: string;
  type: string;
  filters: { id: number; name: string }[];
}

interface Props {
  events: TimelineEventData[];
}

const INITIAL_COUNT = 3;
const THRESHOLD = 5;

const TimelineWithLoadMore = ({ events }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(false);
  const { activeFilter } = useFilter();

  const showTruncated = !expanded && events.length >= THRESHOLD;
  const visibleEvents = showTruncated ? events.slice(0, INITIAL_COUNT + 1) : events;

  return (
    <div>
      <Timeline
        previewFromIndex={showTruncated ? INITIAL_COUNT : undefined}
        onPreviewComplete={() => setButtonVisible(true)}
      >
        {visibleEvents.map((event) => {
          const filterMatch = activeFilter
            ? event.filters.some((f) => f.name === activeFilter)
            : null;
          return (
            <TimelineItem
              key={event.id}
              date={event.date}
              header={event.header}
              description={event.description}
              type={event.type}
              filterMatch={filterMatch}
            />
          );
        })}
      </Timeline>

      {showTruncated && (
        <div className="mx-[4%] -mt-2 relative z-10" style={{ minHeight: "3.5rem" }}>
          {buttonVisible && <motion.button
            onClick={() => setExpanded(true)}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            style={{ background: "transparent", border: "none" }}
            className="w-full flex items-center gap-6 cursor-pointer py-4 px-2 group"
          >
            {/* Left lines */}
            <div className="flex-1 flex flex-col items-end gap-1.5">
              <div className="h-0.5 w-full bg-red-500 rounded-full" />
              <div className="h-0.5 w-[82%] bg-red-500 rounded-full" />
            </div>

            <span className="text-sm font-medium tracking-widest uppercase text-(--foreground) whitespace-nowrap group-hover:text-red-400 transition-colors duration-200">
              Load More
            </span>

            {/* Right lines (mirrored) */}
            <div className="flex-1 flex flex-col items-start gap-1.5">
              <div className="h-0.5 w-full bg-red-500 rounded-full" />
              <div className="h-0.5 w-[82%] bg-red-500 rounded-full" />
            </div>
          </motion.button>}
        </div>
      )}
    </div>
  );
};

export default TimelineWithLoadMore;
